/**
 * Draft handoff between the `client_create_agent` client tool and the
 * Agent editor.
 *
 * The tool handles BOTH create and update:
 *
 * - **Create**: the tool stashes a JSON draft (no ``target_agent_id``)
 *   under the storage key below and navigates the user to ``/agents/new``.
 *   The editor consumes the draft once on mount and clears the slot, so
 *   refreshing the page or opening ``/agents/new`` directly from another
 *   entry point produces the usual empty form.
 *
 * - **Update**: the tool resolves the caller-named agent to an id in the
 *   browser, stashes a draft carrying that ``target_agent_id`` plus
 *   whatever fields the model supplied, and navigates to the agent's edit
 *   route (``/agents/:agentId``). The editor overlays the draft over the
 *   fetched agent's form — provided fields replace, omitted fields keep the
 *   saved values.
 *
 * {@link claimAgentDraftFor} is the only supported way to read the stash;
 * it owns which drafts belong to which route.
 *
 * The shape is wire-friendly (matches the JSON the LLM emits) rather
 * than identical to the editor's `FormState`. Mapping happens at the
 * editor's seam — see `buildRequestTemplate.ts`.
 *
 * SessionStorage is a trust boundary: values can be tampered with via
 * DevTools, browser extensions, or left behind by an older build with a
 * different schema. The consume path runs a runtime shape validator so
 * the editor's lazy state initializer never sees a malformed payload.
 */

const AGENT_DRAFT_STORAGE_KEY = "roboto.agent-draft";

/**
 * Label-keyed description map for a triage goal. Mirrors the wire shape
 * the server stores (`label_vocabulary: Record<label, description>`)
 * so the LLM can produce it directly without an array-of-objects detour.
 */
export interface AgentDraftDatasetTriageGoal {
  goal_type: "dataset_triage";
  label_vocabulary: Record<string, string>;
}

export interface AgentDraftDatasetSummaryGoal {
  goal_type: "dataset_summary";
  summary_format_spec_prompt?: string;
}

/**
 * Wire shape for a ``create_events`` goal in the LLM-emitted draft.
 *
 * Both vocabularies are ``Record<key, description>`` so the LLM can emit them
 * as plain JSON objects (matching the wire format the server ultimately
 * stores). The editor flattens them to ordered row arrays for stable React
 * keys; see ``draftGoalToFormGoal`` in ``buildRequestTemplate.ts``.
 *
 * ``collection_enabled`` is a boolean rather than a collection id because the
 * draft is produced before launch — the actual collection is picked in the
 * editor's launch flow via the implicit ``{{collection.id}}`` variable.
 */
export interface AgentDraftCreateEventsGoal {
  goal_type: "create_events";
  event_vocabulary: Record<string, string>;
  tag_vocabulary?: Record<string, string>;
  collection_enabled?: boolean;
  event_focus_prompt?: string;
}

export type AgentDraftGoal =
  | AgentDraftDatasetSummaryGoal
  | AgentDraftDatasetTriageGoal
  | AgentDraftCreateEventsGoal;

export interface AgentDraftVariable {
  name: string;
  description?: string;
  default?: string;
}

/**
 * One skill the drafted agent should be able to invoke.
 *
 * Carries a resolved `skill_id` rather than the name the model supplied:
 * the tool resolves names before stashing, so a name that doesn't exist (or
 * that names a private skill the server would reject) fails as a tool error
 * the model can act on, instead of an editor that silently drops it.
 *
 * `version` omitted or `null` tracks the skill's latest version, which is
 * what the editor's own Skills picker defaults to.
 */
export interface AgentDraftSkill {
  skill_id: string;
  version?: number | null;
}

/**
 * Partial Agent specification handed from the client tool to the editor.
 *
 * Every field is optional because the LLM should be allowed to emit
 * what it knows and let the user fill the rest in the editor. The
 * editor maps missing fields back to its own empty-form defaults.
 */
export interface AgentDraft {
  name?: string;
  description?: string;
  /** Model-profile id. Omit to use the org default. */
  model_profile?: string;
  /** Optional user message seeded into the agent's first turn. */
  kickoff_message?: string;
  goals?: AgentDraftGoal[];
  variables?: AgentDraftVariable[];
  /**
   * Skills the agent's AI may invoke. Replaces the whole set when present,
   * matching how `goals` and `variables` overlay; omit to keep whatever the
   * saved agent already has.
   */
  skills?: AgentDraftSkill[];
  /**
   * Present only on the update handoff: the resolved id of the existing
   * agent this draft should be overlaid onto. Absent on the create
   * handoff. Also the routing key — see {@link claimAgentDraftFor}.
   */
  target_agent_id?: string;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function asGoal(value: unknown): AgentDraftGoal | null {
  if (!isPlainObject(value)) {
    return null;
  }
  if (value.goal_type === "dataset_summary") {
    const prompt = asString(value.summary_format_spec_prompt);
    return prompt === undefined
      ? { goal_type: "dataset_summary" }
      : { goal_type: "dataset_summary", summary_format_spec_prompt: prompt };
  }
  if (value.goal_type === "dataset_triage") {
    // `label_vocabulary` is the field the editor's downstream consumer
    // (`draftToFormState`) iterates via `Object.entries`. A missing or
    // wrong-typed value here is the canonical "malformed stash crashes
    // the editor" failure mode — drop the goal entirely rather than
    // forward a half-formed shape.
    if (!isPlainObject(value.label_vocabulary)) {
      return null;
    }
    const vocab: Record<string, string> = {};
    for (const [label, description] of Object.entries(value.label_vocabulary)) {
      if (typeof description === "string") {
        vocab[label] = description;
      }
    }
    return { goal_type: "dataset_triage", label_vocabulary: vocab };
  }
  if (value.goal_type === "create_events") {
    // `event_vocabulary` is required and is what `draftGoalToFormGoal`
    // iterates via `Object.entries`. Same crash-proofing rationale as the
    // triage branch above: drop the goal entirely on a malformed stash
    // rather than letting the editor's lazy state initializer throw.
    if (!isPlainObject(value.event_vocabulary)) {
      return null;
    }
    const eventVocab: Record<string, string> = {};
    for (const [name, description] of Object.entries(value.event_vocabulary)) {
      if (typeof description === "string") {
        eventVocab[name] = description;
      }
    }
    const goal: AgentDraftCreateEventsGoal = {
      goal_type: "create_events",
      event_vocabulary: eventVocab,
    };
    if (isPlainObject(value.tag_vocabulary)) {
      const tagVocab: Record<string, string> = {};
      for (const [tag, description] of Object.entries(value.tag_vocabulary)) {
        if (typeof description === "string") {
          tagVocab[tag] = description;
        }
      }
      goal.tag_vocabulary = tagVocab;
    }
    if (typeof value.collection_enabled === "boolean") {
      goal.collection_enabled = value.collection_enabled;
    }
    const focus = asString(value.event_focus_prompt);
    if (focus !== undefined) {
      goal.event_focus_prompt = focus;
    }
    return goal;
  }
  return null;
}

function asVariable(value: unknown): AgentDraftVariable | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const name = asString(value.name);
  if (name === undefined || name === "") {
    return null;
  }
  const entry: AgentDraftVariable = { name };
  const description = asString(value.description);
  if (description !== undefined) {
    entry.description = description;
  }
  const defaultValue = asString(value.default);
  if (defaultValue !== undefined) {
    entry.default = defaultValue;
  }
  return entry;
}

function asSkill(value: unknown): AgentDraftSkill | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const skillId = asString(value.skill_id);
  if (skillId === undefined || skillId === "") {
    return null;
  }
  // A non-numeric version collapses to "latest" rather than dropping the
  // whole entry: the attachment is still the useful part, and the editor's
  // picker treats null as track-latest anyway.
  return typeof value.version === "number"
    ? { skill_id: skillId, version: value.version }
    : { skill_id: skillId };
}

/**
 * Coerce arbitrary parsed JSON into an {@link AgentDraft}.
 *
 * Returns `null` if the value isn't a plain object. Otherwise returns
 * a draft that contains *only* fields whose runtime shape matches the
 * type — wrong-typed fields are dropped, wrong-typed goal / variable
 * entries are dropped from their arrays. This means a partially-corrupt
 * stored draft yields a partially-populated form rather than a crash,
 * and the editor's existing validation surfaces any remaining gaps.
 *
 * Exposed for tests; callers should use {@link consumeAgentDraft}.
 */
export function parseAgentDraftShape(value: unknown): AgentDraft | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const draft: AgentDraft = {};
  const name = asString(value.name);
  if (name !== undefined) {
    draft.name = name;
  }
  const description = asString(value.description);
  if (description !== undefined) {
    draft.description = description;
  }
  const modelProfile = asString(value.model_profile);
  if (modelProfile !== undefined) {
    draft.model_profile = modelProfile;
  }
  const kickoff = asString(value.kickoff_message);
  if (kickoff !== undefined) {
    draft.kickoff_message = kickoff;
  }
  // String-guarded like the other scalars: a tampered / stale stash that
  // carries a non-string ``target_agent_id`` must not be forwarded, or the
  // editor's targeted-consume check would compare a route id against a
  // non-string and mis-handle the handoff.
  const targetAgentId = asString(value.target_agent_id);
  if (targetAgentId !== undefined) {
    draft.target_agent_id = targetAgentId;
  }
  if (Array.isArray(value.goals)) {
    const goals: AgentDraftGoal[] = [];
    for (const raw of value.goals) {
      const goal = asGoal(raw);
      if (goal !== null) {
        goals.push(goal);
      }
    }
    if (goals.length > 0) {
      draft.goals = goals;
    }
  }
  if (Array.isArray(value.variables)) {
    const variables: AgentDraftVariable[] = [];
    for (const raw of value.variables) {
      const variable = asVariable(raw);
      if (variable !== null) {
        variables.push(variable);
      }
    }
    if (variables.length > 0) {
      draft.variables = variables;
    }
  }
  if (Array.isArray(value.skills)) {
    const skills: AgentDraftSkill[] = [];
    for (const raw of value.skills) {
      const skill = asSkill(raw);
      if (skill !== null) {
        skills.push(skill);
      }
    }
    // Unlike goals / variables, an empty result is kept when the stash
    // carried an array: "detach every skill" is a meaningful edit, and
    // dropping it would silently preserve the skills the user asked to
    // remove. Only a non-array (never written by this channel) is ignored.
    draft.skills = skills;
  }
  return draft;
}

// ---------------------------------------------------------------------------
// Consume cache
//
// React 18 StrictMode (dev) double-invokes render-phase functions —
// component bodies and `useState` initializers — to surface impure ones. The
// consume side effect ("read sessionStorage then remove the slot") is not
// idempotent across that: the first invocation reads the draft and clears the
// slot, and the second sees an empty slot and would return `null`, discarding
// the draft the first one found. (This is the render double-call, not the
// separate mount → unmount → remount cycle StrictMode applies to *effects*;
// the two calls happen back-to-back inside one synchronous render.)
//
// The cache below preserves the consume result across one short window
// (current task + queued macrotask) so the second invocation returns
// the same draft. A subsequent fresh visit to the editor (after a true
// navigation away and back) sees the cache cleared and reads from
// sessionStorage normally. `stashAgentDraft` also invalidates the cache
// so any new write supersedes an older cached read.
// ---------------------------------------------------------------------------

interface ConsumeCache {
  value: AgentDraft | null;
}

let consumeCache: ConsumeCache | undefined = undefined;
let consumeCacheClearScheduled = false;

function scheduleConsumeCacheClear(): void {
  if (consumeCacheClearScheduled) {
    return;
  }
  consumeCacheClearScheduled = true;
  // setTimeout(0) fires after the current task and any microtasks it
  // queues. React's StrictMode synthetic remount completes within that
  // same task, so the second mount sees the cache; visits that happen
  // later (after navigation) start a new task and find the cache
  // cleared. `requestAnimationFrame` would also work but isn't
  // available in every test runtime.
  setTimeout(() => {
    consumeCache = undefined;
    consumeCacheClearScheduled = false;
  }, 0);
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

/**
 * Write the draft to sessionStorage. Silently no-ops if storage is
 * unavailable (e.g. private-mode quirks); the editor handles the
 * resulting empty draft as if the user had opened the page directly.
 */
export function stashAgentDraft(draft: AgentDraft): void {
  try {
    sessionStorage.setItem(AGENT_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Storage unavailable — fall back to the editor's empty form.
  }
  // Invalidate any cached read so the next `consumeAgentDraft` sees
  // this stash, not the prior visit's value.
  consumeCache = undefined;
}

/**
 * Read, validate, and clear the pending draft.
 *
 * Returns `null` when no draft is pending, when the stored payload
 * fails to parse, or when the parsed value isn't a plain object. Any
 * nested field whose runtime shape doesn't match its declared type is
 * dropped silently via {@link parseAgentDraftShape}; the editor
 * surfaces the gaps via its own validation rather than crashing.
 *
 * The slot is cleared even on parse failure so a corrupt entry can't
 * wedge subsequent editor visits. The result is briefly cached at the
 * module level — see the comment block above — so a React 18
 * StrictMode synthetic remount sees the same draft as the first mount
 * rather than an empty slot.
 */
export function consumeAgentDraft(): AgentDraft | null {
  if (consumeCache !== undefined) {
    return consumeCache.value;
  }
  return commitConsume(readStashedDraft());
}

/**
 * Clear the stash and cache `parsed` as the consumed value, returning it.
 *
 * Takes the already-parsed draft so a caller that has read the stash (see
 * {@link claimAgentDraftFor}) doesn't parse it a second time.
 */
function commitConsume(parsed: AgentDraft | null): AgentDraft | null {
  // Clear unconditionally — including on a parse failure, so a corrupt
  // entry can't wedge subsequent editor visits. Removing an absent key is
  // a no-op.
  try {
    sessionStorage.removeItem(AGENT_DRAFT_STORAGE_KEY);
  } catch {
    // Best-effort clear.
  }
  consumeCache = { value: parsed };
  scheduleConsumeCacheClear();
  return parsed;
}

/**
 * Read and validate the pending draft without clearing it.
 *
 * Shared by {@link consumeAgentDraft} and {@link peekAgentDraft} so the
 * storage/parse/error-swallow rules can't drift between the two.
 */
function readStashedDraft(): AgentDraft | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(AGENT_DRAFT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  try {
    return parseAgentDraftShape(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * Read the pending draft **without clearing it**.
 *
 * Module-private: reading without consuming is only safe as half of the
 * match-then-consume rule in {@link claimAgentDraftFor}, which is the
 * supported entry point. A caller that peeks and forgets to consume leaves
 * a draft that re-applies on every later visit.
 *
 * Cache-aware for the same reason {@link consumeAgentDraft} is: React 18
 * StrictMode double-invokes the editor's render-phase claim. Once the first
 * invocation has consumed a matching draft (populating the module cache and
 * clearing storage), the second peek must still return that draft rather than
 * reading an empty slot.
 */
function peekAgentDraft(): AgentDraft | null {
  if (consumeCache !== undefined) {
    return consumeCache.value;
  }
  return readStashedDraft();
}

/** Which editor route is asking for the pending draft. */
export interface AgentDraftRoute {
  mode: "create" | "edit";
  /** The routed agent id. Required for a claim in edit mode. */
  agentId?: string;
}

/**
 * Claim the pending draft if — and only if — it belongs to this route.
 *
 * This is the match-then-consume rule for the draft channel, and the only
 * supported way to read it:
 *
 * - **Edit route**: claims a draft whose `target_agent_id` matches
 *   `agentId`. A draft targeting a *different* agent, a create draft
 *   (no target), or a mount with no `agentId` is left in the stash — a
 *   foreign draft must never mutate the agent the user happens to be on.
 * - **Create route**: claims only a create draft. An update draft is left
 *   alone so a proposal aimed at an existing agent can't quietly become a
 *   brand-new one.
 *
 * A claimed draft is cleared from storage, so it applies at most once.
 * Returns `null` when nothing was claimed.
 *
 * The match is on identity, not recency: nothing binds a stash to the
 * navigation that produced it, and sessionStorage outlives that navigation.
 * So a draft whose handoff never landed (the user clicked away before the
 * route committed) stays pending and is claimed the next time that same agent's
 * edit route is opened, however much later in the tab session. Bounding that
 * would need the stash to carry the navigation it belongs to.
 */
export function claimAgentDraftFor(route: AgentDraftRoute): AgentDraft | null {
  const pending = peekAgentDraft();
  if (pending === null) {
    return null;
  }
  const belongsToRoute =
    route.mode === "edit"
      ? pending.target_agent_id !== undefined &&
        route.agentId !== undefined &&
        pending.target_agent_id === route.agentId
      : pending.target_agent_id === undefined;
  return belongsToRoute ? commitConsume(pending) : null;
}

/**
 * Internal hook for tests — exported so tests can reset state between
 * cases without poking at the storage key directly.
 */
export const __AGENT_DRAFT_STORAGE_KEY_FOR_TESTS: string =
  AGENT_DRAFT_STORAGE_KEY;

/**
 * Internal hook for tests — synchronously clear the module-level
 * consume cache so each test starts from a clean state. Tests
 * normally let the setTimeout-based clear handle this, but a tight
 * sequence of stash+consume cycles in one test needs an explicit
 * reset to avoid the previous test's cache bleeding through.
 */
export function __resetAgentDraftCacheForTests(): void {
  consumeCache = undefined;
  consumeCacheClearScheduled = false;
}
